import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

import { allIngredientReferencesKey } from "./useAllIngredientReferences";

export type IngredientReferenceScoreWrite = {
    scoreId: number;
    value: number | null;
    /**
     * Campaign IRI (e.g. `/api/campaigns/2`). The backend keys score uniqueness by
     * (ingredient_reference_id, score_id, campaign_id) and `setScores()` orphans any
     * (scoreId, campaign) pair missing from the payload, so callers MUST round-trip
     * every cell they want to keep.
     */
    campaign: IRI;
};

/**
 * Minimal junction-row payload accepted by API Platform. The backend resolves the
 * `Company` entity from the IRI; sending only `{ company }` matches the
 * `IngredientCompany` pattern and avoids leaking the junction `id` for new rows.
 * Existing associations can be PATCHed by including their `@id`.
 */
export type IngredientReferenceCompanyWrite = {
    "@id"?: IRI;
    organization: IRI;
};

export type IngredientReferenceFromProductWrite = {
    /** Product IRI (e.g. `/api/products/123`) — required by API Platform denormalization. */
    product: IRI;
    name?: string;
    code?: string | null;
    description?: string | null;
    cpcCode?: string | null;
    defaultOrigin?: string | null;
    organic?: boolean | null;
    fresh?: boolean | null;
    animal?: boolean | null;
    scores?: IngredientReferenceScoreWrite[];
};

export type IngredientReferenceWrite = {
    id?: number;
    name: string;
    code?: string | null;
    description?: string | null;
    cpcCode?: string | null;
    defaultOrigin?: string | null;
    organic?: boolean | null;
    fresh?: boolean | null;
    animal?: boolean | null;
    scores: IngredientReferenceScoreWrite[];
    /**
     * Optional on the wire — when present, the backend syncs the OneToMany collection
     * (orphan-removal). Omit it from the payload when the user is not the owner so we
     * do not accidentally clear the existing associations.
     */
    ingredientReferenceOrganizations?: IngredientReferenceCompanyWrite[];
};

type CreateIngredientReference = {
    ingredientReference: IngredientReferenceWrite;
};

type CreateIngredientReferenceFromProduct = {
    payload: IngredientReferenceFromProductWrite;
};

export const useCreateIngredientReference = () => {
    const queryClient = useQueryClient();

    const query = useMutation({
        mutationFn: async (options: CreateIngredientReference | CreateIngredientReferenceFromProduct) => {
            if ("payload" in options) {
                return loggedInApiCall.post("/api/ingredient-reference-from-product", options.payload);
            }

            const { ingredientReference } = options;

            const response =
                ingredientReference.id === undefined ?
                    await loggedInApiCall.post("/api/ingredient-references", ingredientReference)
                :   await loggedInApiCall.put(
                        "/api/ingredient-references/" + ingredientReference.id,
                        ingredientReference,
                    );

            return response;
        },
        onSuccess: () => queryClient.invalidateQueries([allIngredientReferencesKey]),
    });

    return query;
};
